import { Box, styled } from "@mui/material"

// Same square card everywhere a file attachment shows up in the chat — the compose bar's pre-send
// preview and an already-sent message's attachment display both render this, so they can't drift apart.
// Every visual rule (including font sizes) is fixed here, not passed in per-instance via sx.
const StyledAttachmentCard = styled(Box)(({ theme }) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    // Small enough that all 5 (the max per message) fit on one row in the compose bar instead of
    // wrapping onto a second line.
    width: 88,
    height: 88,
    flexShrink: 0,
    // Literal value (the general radius scale has no step between 8px and 16px) — kept in sync
    // with the same value in Message/styled.ts so the card is equally rounded as the message bubble.
    borderRadius: "12px",
    backgroundColor: (theme as any).palette.neutral.darkest,
    padding: theme.spacing(0.75),
    "& .chat-attachment-filename": {
        fontSize: "0.75rem",
        color: (theme as any).palette.neutral.lighter,
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        // Takes the bulk of the header row — the remove button (a real flex sibling now, not an
        // overlay) claims its own small, fixed slot instead of sharing space with the text.
        flex: "1 1 auto",
        minWidth: 0,
    },
    "& .chat-attachment-badge-label": {
        fontSize: "0.625rem",
    },
}))

// Filename + remove button, side by side with real breathing room between them — replaces the old
// "absolutely-position the button over the text" approach, which left them touching.
const StyledAttachmentHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
}))

const StyledAttachmentBadge = styled(Box)(({ theme }) => ({
    // fit-content so a short "PDF" stays a small pill, but capped so "PDF · 2.34 MB" doesn't push
    // past the card's own edge — it ellipsizes instead (via noWrap on the Typography inside).
    // border-box so that 100% cap includes this box's own padding — the app doesn't set border-box
    // globally, and without it the padding was added on top of the 100%, overflowing the card.
    boxSizing: "border-box",
    width: "fit-content",
    maxWidth: "100%",
    // Only this item centers — the filename above stays stretched to the card's full width (it
    // needs that for its line-clamp to wrap correctly instead of overflowing).
    alignSelf: "center",
    backgroundColor: (theme as any).palette.neutral.dark,
    color: (theme as any).palette.neutral.lighter,
    borderRadius: (theme as any).shape.border.general.radius[4],
    padding: theme.spacing(0.25, 0.75),
}))

export { StyledAttachmentCard, StyledAttachmentHeader, StyledAttachmentBadge }
